import React from 'react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory';
import type { DocNode } from '@atlaskit/adf-schema';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';

import type { GenerateImageProps } from '@atlassian/ai-image-generation';
import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import type { AdditionalContext, ContentLocation } from '@atlassian/editor-rovo-bridge';

import { fg } from '@confluence/feature-gating';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import { useEditorRovoBridgeCommandListener } from './hooks/useEditorRovoBridgeCommandListener.tsx';

export interface UseRovoBridgeCommandListenerParams {
	additionalContext?: AdditionalContext;
	editorAPI: PublicPluginAPI<[AnalyticsPlugin, AiStreamingOrchestratorPlugin]> | undefined | null;
	onTitleChange?: (title: string) => void;
	contentId: string;
	contentLocation: ContentLocation;
	setEditorDisabled?: (disabled: boolean) => void;
	forceDraftSync?: () => Promise<DocNode>;
	generateAIHeaderImage?: (props: GenerateImageProps) => Promise<string | undefined>;
	fakeGenerateAIHeaderImage?: () => Promise<void>;
	mediaProvider?: Promise<MediaProvider>; // Image processor for handling media collection IDs
	setTitlePlaceholderState?: (isLoading: boolean) => void;
	useGenericEditorSkill?: boolean;
	/** Called when a streaming operation completes (e.g. create, deep-research-create) */
	onStreamEnd?: () => void;
	/** Called after rovoBridgeAPI.ready() and ContentReadyCommand have been sent,
	 * guaranteeing both sides of the bridge are ready to communicate. */
	onBridgeReady?: () => void;
	/** Whether the editor is currently in preview mode. When true, content updates
	 * from Rovo chat will be blocked and a warning flag shown to the user. */
	isPreviewMode?: boolean;
}

export type ExtraCommandProps = Pick<
	UseRovoBridgeCommandListenerParams,
	'forceDraftSync' | 'generateAIHeaderImage'
> & {
	reuploadImages?: (adfDoc: DocNode) => Promise<void>;
};

const EditorRovoBridgeCommandListenerImpl = (
	props: UseRovoBridgeCommandListenerParams & WithFlagsProps,
) => {
	useEditorRovoBridgeCommandListener(props);
	return null;
};

const RovoBridgeCommandListenerInternal = (
	props: UseRovoBridgeCommandListenerParams & WithFlagsProps,
) => {
	if (!fg('aifc_create_enabled')) {
		return null;
	}

	return <EditorRovoBridgeCommandListenerImpl {...props} />;
};

export const RovoBridgeCommandListener = withFlags(RovoBridgeCommandListenerInternal);
